import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useRef } from 'react';

import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { useUnsavedChangesStore } from '../unsaved-changes.store';
import { UnsavedChangesArea } from '../unsaved-changes.types';

export const useUnsavedChanges = (area: UnsavedChangesArea | UnsavedChangesArea[]) => {
  const discardChangesModal = useGlobalDialog(GlobalDialogType.DiscardChangesModal);
  const lastAreaRef = useRef<UnsavedChangesArea[]>([]);

  const memoizedAreas = useMemo(() => {
    const areas = Array.isArray(area) ? area : [area];
    const normalizedAreas = [...new Set(areas)].sort();

    if (!isEqual(lastAreaRef.current, normalizedAreas)) {
      lastAreaRef.current = normalizedAreas;
    }

    return lastAreaRef.current;
  }, [area]);

  const withNavigationGuard = useCallback(
    (callback: () => void) => {
      const hasChanges = memoizedAreas.some((area) => useUnsavedChangesStore.getState().checkAreaChanges(area));

      if (hasChanges) {
        discardChangesModal.open({
          payload: { onDiscardChanges: callback },
        });
      } else {
        callback();
      }
    },
    [memoizedAreas],
  );

  const registerChangeDetector = useCallback(
    (detector: () => boolean) => {
      const state = useUnsavedChangesStore.getState();

      memoizedAreas.forEach((area) => {
        state.registerChangeDetector(area, detector);
      });

      return () => {
        memoizedAreas.forEach((area) => {
          state.unregisterChangeDetector(area, detector);
        });
      };
    },
    [memoizedAreas],
  );

  const unregisterChangeDetector = useCallback(
    (detector: () => boolean) => {
      memoizedAreas.forEach((area) => {
        useUnsavedChangesStore.getState().unregisterChangeDetector(area, detector);
      });
    },
    [memoizedAreas],
  );

  return {
    withNavigationGuard,
    registerChangeDetector,
    unregisterChangeDetector,
  };
};
